/**
 * TankVariantEditor — shape: tank_variant (SS_WATER_TANK).
 * Edits the shared `spec` (title + KPI tiles + spec rows) PLUS a variants table
 * (grade × thickness × insulation → tds / heat-loss / material / modelName).
 * The remaining helper keys (capacities, dimensionsByCapacity, …) are preserved
 * as-is on the data object and round-trip untouched through onChange.
 */

import {
  CategorySpecEditor,
  FieldLabel,
  SpecInput,
  MiniLabel,
  AddRowButton,
  iconBtnStyle,
  inputStyle,
  type CategorySpecShape,
} from './specPrimitives';
import { Trash2 } from 'lucide-react';

interface TankVariant {
  grade: string;
  thicknessMm: number;
  insulationMm: number;
  material: string;
  tds: number;
  heatLoss60: string;
  heatLoss100: string;
  modelName: string;
}

export function TankVariantEditor({
  data,
  tokens,
  onChange,
}: {
  data: any;
  tokens: string[];
  onChange: (next: any) => void;
}) {
  const spec: CategorySpecShape = data?.spec ?? { title: '', kpiTiles: [], specRows: [] };
  const variants: TankVariant[] = Array.isArray(data?.variants) ? data.variants : [];

  const setSpec = (next: CategorySpecShape) => onChange({ ...data, spec: next });
  const setVariants = (next: TankVariant[]) => onChange({ ...data, variants: next });

  const patch = (i: number, p: Partial<TankVariant>) =>
    setVariants(variants.map((v, idx) => (idx === i ? { ...v, ...p } : v)));
  const remove = (i: number) => setVariants(variants.filter((_, idx) => idx !== i));
  const add = () =>
    setVariants([
      ...variants,
      {
        grade: '',
        thicknessMm: 0,
        insulationMm: 60,
        material: '',
        tds: 0,
        heatLoss60: '',
        heatLoss100: '',
        modelName: '',
      },
    ]);

  return (
    <>
      <CategorySpecEditor spec={spec} tokens={tokens} onChange={setSpec} />

      <FieldLabel label="Variants" hint="grade × thickness × insulation → resolved KPI values">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {variants.map((v, i) => (
            <div
              key={i}
              style={{
                border: '1px solid var(--line)',
                borderRadius: 10,
                padding: 12,
                background: 'var(--ivory-0)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: 'var(--ink-3)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Variant {i + 1}
                </span>
                <button type="button" onClick={() => remove(i)} title="Remove variant" style={iconBtnStyle('#b91c1c')}>
                  <Trash2 size={13} />
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <div>
                  <MiniLabel>Grade</MiniLabel>
                  <SpecInput value={v.grade ?? ''} onChange={(x) => patch(i, { grade: x })} placeholder="e.g. 316L-1.0" />
                </div>
                <div>
                  <MiniLabel>Thickness (mm)</MiniLabel>
                  <input
                    type="number"
                    value={Number.isFinite(v.thicknessMm) ? v.thicknessMm : ''}
                    onChange={(e) => patch(i, { thicknessMm: Number(e.target.value) })}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <MiniLabel>Insulation (mm)</MiniLabel>
                  <input
                    type="number"
                    value={Number.isFinite(v.insulationMm) ? v.insulationMm : ''}
                    onChange={(e) => patch(i, { insulationMm: Number(e.target.value) })}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <MiniLabel>TDS (ppm)</MiniLabel>
                  <input
                    type="number"
                    value={Number.isFinite(v.tds) ? v.tds : ''}
                    onChange={(e) => patch(i, { tds: Number(e.target.value) })}
                    style={inputStyle}
                  />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <MiniLabel>Material</MiniLabel>
                  <SpecInput value={v.material ?? ''} onChange={(x) => patch(i, { material: x })} />
                </div>
                <div>
                  <MiniLabel>Heat loss (60mm)</MiniLabel>
                  <SpecInput value={v.heatLoss60 ?? ''} onChange={(x) => patch(i, { heatLoss60: x })} />
                </div>
                <div>
                  <MiniLabel>Heat loss (100mm)</MiniLabel>
                  <SpecInput value={v.heatLoss100 ?? ''} onChange={(x) => patch(i, { heatLoss100: x })} />
                </div>
                <div>
                  <MiniLabel>Model name</MiniLabel>
                  <SpecInput value={v.modelName ?? ''} onChange={(x) => patch(i, { modelName: x })} />
                </div>
              </div>
            </div>
          ))}
          <AddRowButton onClick={add} label="Add variant" />
        </div>
      </FieldLabel>
    </>
  );
}
